/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseService, AgFns, GridOptionsExt, ISortModel, NotificationService, ProxAgFns, StatusService, SaveAndQueryComponent } from '@core';
import { Account, AccountUser, ActiveStatus, ActiveStatusEnum, Gender, ProximityRightEnum, ProximityUserNote } from '@models';

import { UtilFns } from '@utils';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountActivationService } from '../services/account-activation.service';
import { UserProgramTimelineDialogComponent, UserProgramTimelineDialogParams } from './user-program-timeline-dialog.component';

@Component({
  selector: 'prox-account-user-list',
  templateUrl: './account-user-list.component.html',
  standalone: false,
})
export class AccountUserListComponent extends SaveAndQueryComponent {
  accountId?: string;
  accountUsers: AccountUser[] = [];
  activeStatuses: ActiveStatus[] = [];
  auGridOptions: GridOptionsExt = {};
  account!: Account;
  editLabel = 'Edit';
  editLabelWidth = 50;
  isFirstDataRendered = false;

  allGenders: Gender[] = [];
  showInactive = false;

  /** Sentinel option id for "No PUGs Assigned". Deliberately not a real PUG id. */
  readonly noPugOptionId = '--no-pug--';
  pugFilterOptions: { id: string; name: string }[] = [];
  selectedPugFilterIds: string[] = [];

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    public activationService: AccountActivationService,
    override dbQueryService: AccountDbQueryService,
    override dbSaveService: AccountDbSaveService,
    private statusService: StatusService,
    public pngDialogService: DialogService,
    private notificationService: NotificationService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.account = await this.dbQueryService.getAccountById(this.accountId);
    UtilFns.assertNonNull(this.account, 'Account');
    this.activeStatuses = await this.dbQueryService.getAll(ActiveStatus);

    this.allGenders = await this.dbQueryService.getAll(Gender);

    // Grid-wide PUG filter options. Hold/Phaseout PUGs stay selectable because they still have
    // users attached; only Inactive is excluded.
    const pugs = await this.dbQueryService.getSelectableProgramUserGroups(this.accountId);
    this.pugFilterOptions = [
      { id: this.noPugOptionId, name: 'No PUGs Assigned' },
      ...pugs.map((p) => ({ id: p.id, name: p.name })),
    ];

    this.editLabel = this.account.getStatusId() > 2 ? 'Read Only' : 'Edit';
    this.editLabelWidth = this.account.getStatusId() > 2 ? 100 : 50;

    this.auGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAuGridReady,
      onRowSelected: this.onAuRowSelected,
      onModelUpdated: () => this.clearCurrentSelection(false),
      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.clearCurrentSelection(true);
        }
      },
      onFirstDataRendered: () => (this.isFirstDataRendered = true),
      rowModelType: 'serverSide',
    });

    AgFns.captureGridRouteParams(this.auGridOptions, this.route, 'id');
    this.setTitle('Account Users for ' + this.account.name);
    this.isPageReady = true;
  }

  async updateDatasource() {
    if (this.auGridOptions.apiX == null) {
      return;
    }
    // p-multiSelect's clear button sets the model to null rather than [], so normalize defensively.
    const selectedPugIds = this.selectedPugFilterIds ?? [];
    const pugIds = selectedPugIds.filter((id) => id !== this.noPugOptionId);
    const includeUsersWithNoPug = selectedPugIds.includes(this.noPugOptionId);
    const ds = AgFns.buildDatasource(
      () =>
        this.dbQueryService.createAccountUsersQuery(
          <string>this.accountId,
          this.showInactive,
          pugIds,
          includeUsersWithNoPug,
        ),
      // () => this.dbQueryService.createAccountUsersForAccountAdminIdQuery(this.accountAdminId, false),
      'id',
    );
    this.auGridOptions.apiX.setGridOption('serverSideDatasource', ds);
    AgFns.applyGridRouteParams(this.auGridOptions);
  }

  isReadOnly() {
    // return !this.hasRight(ProximityRightEnum.CanManageAccountOrganization) || this.account.getStatusId() > 2 || !this.isFirstDataRendered;
    return !this.hasRight(ProximityRightEnum.CanManageAccountOrganization) || this.account.getStatusId() > 2;
  }

  statusMessage() {
    if (!this.hasRight(ProximityRightEnum.CanManageAccountOrganization)) {
      return 'Not authorized';
    }

    let msg = '';
    if (this.account?.activeStatusId == ActiveStatusEnum.Active) {
      return 'Account is Active.  Account Users are Editable.';
    } else if (this.account?.activeStatusId == ActiveStatusEnum.Hold) {
      msg = 'Account is On-Hold and is Editable';
    } else {
      return 'Account is ' + this.account?.activeStatus.name + ' and is Not Editable';
    }
    return msg;
  }

  onAuGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps('', this.onEdit.bind(this), {
          label: this.editLabel,
        }),
        ...AgFns.fixWidth(this.editLabelWidth),
        suppressHeaderMenuButton: true,
      },
      // {
      //   ...AgFns.createButtonProps('', this.onLedgers.bind(this), {
      //     label: 'Ledgers',
      //   }),
      //   width: this.editLabelWidth + 20,
      //   maxWidth: this.editLabelWidth + 20,
      // },
      {
        ...AgFns.createButtonProps('', this.onSendInvite.bind(this), {
          label: 'Send Invite',
          canDisplay: () => this.account.allowAccountUserAccess,
        }),
        ...AgFns.fixWidth(100),
        suppressHeaderMenuButton: true,
      },
      {
        ...AgFns.createButtonProps('', this.onShowPlan.bind(this), {
          label: 'Show Plan',
        }),
        ...AgFns.fixWidth(95),
        suppressHeaderMenuButton: true,
      },
      {
        headerName: 'Last Name',
        field: 'proximityUser.lastName',
        ...AgFns.flexWidth(150),
        filter: 'agTextColumnFilter',
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        headerName: 'First Name',
        field: 'proximityUser.firstName',
        ...AgFns.flexWidth(150),
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Middle Name',
        field: 'proximityUser.middleName',
        ...AgFns.flexWidth(110),
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Salutation',
        field: 'proximityUser.salutation',
        ...AgFns.flexWidth(100),
      },
      {
        headerName: 'E-Mail',
        field: 'proximityUser.email',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'User Name',
        field: 'proximityUser.username',
        ...AgFns.flexWidth(150),
        filter: 'agTextColumnFilter',
      },
      // {
      //   ...AgFns.createButtonProps('', this.onResetLogin.bind(this), {
      //     label: 'Reset Login',
      //   }),
      //   width: 100,
      //   maxWidth: 100,
      // },
      {
        headerName: 'Program User Group',
        valueGetter: (params) => {
          const au = params.data as AccountUser;
          if (au == null) return;
          return au.programUserGroupMaps.map((x) => x.programUserGroup.name).join(', ');
        },
        sortable: false,
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Gender',
        field: 'gender.name',
        ...AgFns.flexWidth(110),
        filter: 'agSetColumnFilter',
        filterParams: {
          keyCreator: (_) => this.allGenders.map((i) => i.id),
          valueFormatter: (_) => this.allGenders.map((m) => m.name),
        },
      },
      {
        headerName: 'Hire Date',
        field: 'proximityUser.hireDate',
        filter: 'agDateColumnFilter',
        ...AgFns.fixWidth(130),
      },
      // ProxAgFns.getWorkingStatusDef('Working Status', this.statusService),
      {
        ...ProxAgFns.getActiveStatusColDef('proximityUser.activeStatus.name'),
        ...AgFns.fixWidth('status'),
        sortable: false,
      },
      ProxAgFns.getEntityStatusColDef(this.onChangeActiveStatus.bind(this)),
    ];

    const sortModel: ISortModel = [
      { colId: 'proximityUser.lastName', sort: 'asc' },
      { colId: 'proximityUser.firstName', sort: 'asc' },
    ];

    AgFns.initGrid(event, colDefs, sortModel);
    this.updateDatasource();
  }

  async onChangeActiveStatus(accountUser: AccountUser) {
    const valFn = async (selectedStatusId: ActiveStatusEnum, errs: string[]) => {
      if (!accountUser?.entityAspect.validateEntity() || !accountUser.proximityUser.entityAspect.validateEntity()) {
        errs.push('Account User failed validation test.  Fix Account User before changing status.');
      }
      return true;
    };
    const newActiveStatusId = await this.statusService.onChangeActiveStatus(this, accountUser.proximityUser, 'Account User', valFn);
    if (newActiveStatusId != null) {
      const puNote = this.dbSaveService.createEntity(ProximityUserNote, {
        proximityUserId: accountUser.proximityUserId,
        note: `Account User Active Status changed to '${accountUser.proximityUser.activeStatus.name}'`,
        noteTs: new Date(),
        isSystemGenerated: true,
      });
      await this.dbSaveService.saveSelectedChanges([puNote]);

      if (newActiveStatusId === ActiveStatusEnum.Hold) {
        const userLink = `/account/accounts/${this.accountId}/manage/account-users/${accountUser.id}`;
        this.notificationService.add(
          'warn',
          'Account User On Hold',
          `<a href="${userLink}">${accountUser.proximityUser.fullName}</a> is on Hold. Remember to re-activate when editing is complete.`,
          undefined,
          `account-user-hold:${accountUser.id}`,
        );
      } else {
        this.notificationService.resolveByKey(`account-user-hold:${accountUser.id}`);
      }
    }
    this.auGridOptions.apiX?.refreshCells();
  }

  onShowInactiveChanged() {
    this.updateDatasource();
  }

  onPugFilterChanged() {
    // The clear ('X') button emits onClear -- not onChange -- and nulls the model, so both events
    // route here and the model is normalized before the query is rebuilt.
    this.selectedPugFilterIds = this.selectedPugFilterIds ?? [];
    // A narrower filter can leave the grid on a page that no longer exists. Dropping the saved page
    // makes applyGridRouteParams a no-op so the new datasource starts at page 1.
    const gridState = this.auGridOptions.context?.gridState;
    if (gridState != null) {
      gridState.pg = null;
    }
    this.updateDatasource();
  }

  async onAuRowSelected(event: RowSelectedEvent) {
    const au = event.data as AccountUser;
    if (au == null) {
      return;
    }
    if (event.node.isSelected()) {
      this.selectedRow = au;
    } else {
      if (au == this.selectedRow) {
        this.selectedRow = undefined;
      }
    }
    this.updateLocation();
  }

  // async onResetLogin(row: AccountUser) {
  //   await this.dbSaveService.postResetProximityUserLogin(row.proximityUser.id, Roles.AccountUser);
  //   this.toastr.success('Account User Login has been reset for ' + row.proximityUser.name);
  // }

  private async onSendInvite(row: AccountUser) {
    if (!this.account.allowAccountUserAccess) {
      await this.dialogService.warnDialog(
        'Cannot Send Invite',
        'Account User login is disabled for this account. Invites cannot be sent.',
      );
      return;
    }
    if (row.proximityUser.email == null || row.proximityUser.email.trim() === '') {
      await this.dialogService.warnDialog(
        'Cannot Send Invite',
        'Cannot send invite email.  No email address for ' + row.proximityUser.name,
      );
      return;
    }
    const status = await this.dbSaveService.postSendUserInvite(row.proximityUser.id);
    if (status === 'Sent') {
      this.toastr.success('Invite email was sent to ' + row.proximityUser.name);
    } else if (status === 'AlreadyConfirmed') {
      this.toastr.info(
        `${row.proximityUser.name}'s email is already confirmed — no invite was sent.`,
        'Email Already Confirmed',
      );
    }
  }

  updateLocation() {
    this.updateGridRouteParams(this.auGridOptions, this.selectedRow?.id);
  }

  clearCurrentSelection(stayOnPage: boolean = true) {
    if (this.selectedRow == null) {
      return;
    }

    this.updateLocation();
    this.selectedRow = undefined;
  }

  onEdit(row: AccountUser) {
    this.updateGridRouteParams(this.auGridOptions, row.id);
    this.router.navigate(['account/accounts', this.accountId, 'manage', 'account-users', row.id]);
  }

  onAdd() {
    this.router.navigate(['account/accounts', this.accountId, 'manage', 'account-users', 'add']);
  }

  async onUsersImport() {
    this.router.navigate(['import', this.accountId], {
      relativeTo: this.route,
    });
  }

  async onLedgers(row: AccountUser) {
    this.updateGridRouteParams(this.auGridOptions, row.id);
    this.router.navigate(['account/accounts', this.accountId, 'manage', 'account-users', 'ledgers', row.id]);
  }

  onShowPlan(row: AccountUser) {
    const userName = row.proximityUser?.name || 'User';
    this.pngDialogService.open(UserProgramTimelineDialogComponent, {
      header: `Program Access Timeline - ${userName}`,
      width: '1350px',
      height: '600px',
      contentStyle: { overflow: 'hidden' },
      styleClass: 'yellow-header',
      closable: true,
      modal: true,
      resizable: true,
      data: {
        accountUser: row,
      } as UserProgramTimelineDialogParams,
    });
  }
}
